import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { getCesionesListing } from '../../services/cesionesService';
import {
  canAccessCesionesIniciar,
  canAccessCesionesControl,
  canAccessCesionesAprobacion,
  canAccessCesionesBusqueda,
  canAccessCesionesTracking,
  canAccessCesionesRevision,
} from '../../utils/auth';

const CesionesDashboard = ({ user, onLogout, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [cesionesStats, setCesionesStats] = useState({ rechazados: 0, pendientes: 0 });

  const allModules = [
    {
      id: 1,
      title: 'INICIAR CESIÓN',
      icon: 'autorenew',
      description: 'Iniciar una nueva solicitud de cesión de plan de ahorro',
      action: 'iniciarCesion',
      canAccess: canAccessCesionesIniciar,
    },
    {
      id: 2,
      title: 'CONTROL DE CESIONES',
      icon: 'assignment',
      description: 'Consulta y gestiona cesiones rechazadas.',
      action: 'controlCesiones',
      badge: null,
      canAccess: canAccessCesionesControl,
    },
    {
      id: 3,
      title: 'REVISIÓN DE CESIONES',
      icon: 'pending_actions',
      description: 'Listar y revisar cesiones pendientes de aprobación',
      action: 'revisionCesion',
      canAccess: canAccessCesionesRevision,
    },
    {
      id: 4,
      title: 'BÚSQUEDA DE CESIONES',
      icon: 'search',
      description: 'Localizar y consultar cesiones por grupo, orden o ID',
      action: 'busquedaCesiones',
      canAccess: canAccessCesionesBusqueda,
    },
    {
      id: 5,
      title: 'TRACKING DE CESIONES',
      icon: 'history',
      description: 'Registro y seguimiento de todas las actividades de cesiones',
      action: 'trackingCesiones',
      canAccess: canAccessCesionesTracking,
    },
  ];

  const getFilteredModules = () =>
    allModules.filter((m) => m.canAccess && m.canAccess());

  const [modules, setModules] = useState(getFilteredModules);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const rechazados = await getCesionesListing('RECHAZADO', user.ce_ID).catch(() => []);
        const stats = { rechazados: rechazados.length };
        setCesionesStats(stats);
        setModules((prev) =>
          prev.map((m) =>
            m.id === 2 ? { ...m, badge: { rechazados: stats.rechazados } } : m
          )
        );
      } catch {
        setCesionesStats({ rechazados: 0 });
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const getGridClasses = () => {
    const n = modules.length;
    if (n === 1) return 'grid grid-cols-1 gap-6 md:gap-8 max-w-md mx-auto';
    if (n === 2) return 'grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-3xl mx-auto';
    if (n === 3) return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10 max-w-6xl mx-auto';
    if (n === 4) return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto';
    return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 md:gap-8 max-w-7xl mx-auto';
  };

  if (loading) {
    return (
      <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
        <div className="flex items-center justify-center h-screen">
          <HondaLoader />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="px-3 xl:px-4 py-4 xl:py-6">
        <div className="max-w-full mx-auto w-full">
          <div className="mb-5 xl:mb-8 text-center">
            <h2 className="text-xl md:text-2xl xl:text-3xl font-bold text-gradient uppercase tracking-wide flex items-center justify-center gap-2">
              <div className="w-1 h-6 xl:h-8 bg-red-600 rounded-full"></div>
              CESIONES
              <div className="w-1 h-6 xl:h-8 bg-red-600 rounded-full"></div>
            </h2>
            <p className="text-gray-500 mt-1 xl:mt-2 text-xs xl:text-sm md:text-base">
              Gestión integral de transferencias de planes de ahorro
            </p>
          </div>

          <div className={getGridClasses()}>
            {modules.map((module) => (
              <div
                key={module.id}
                className="relative group cursor-pointer transition-transform duration-300 hover:scale-105 hover:z-10"
              >
                <div
                  onClick={() => onNavigate(module.action)}
                  className="h-full min-h-[220px] md:min-h-[260px] xl:min-h-[300px]"
                >
                  <div className="bg-white border border-gray-200 rounded-xl shadow-md group-hover:shadow-xl transition-all duration-300 group-hover:border-red-300 overflow-hidden h-full flex flex-col">
                    <div className="h-1.5 xl:h-2 bg-red-600 group-hover:bg-red-700 transition-colors flex-shrink-0"></div>
                    <div className="p-4 md:p-6 xl:p-8 text-center flex-1 flex flex-col justify-center">
                      <div className="mb-4 xl:mb-6 flex justify-center flex-shrink-0">
                        <div className="w-16 h-16 md:w-20 md:h-20 xl:w-24 xl:h-24 bg-red-50 rounded-full flex items-center justify-center group-hover:bg-red-100 transition-colors shadow-sm">
                          <span className="material-icons text-3xl md:text-4xl xl:text-5xl text-red-600">
                            {module.icon}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-base md:text-lg xl:text-xl font-bold text-gray-800 mb-2 xl:mb-3 group-hover:text-red-700 transition-colors flex-shrink-0">
                        {module.title}
                      </h3>
                      {module.description && (
                        <p className="text-xs md:text-sm xl:text-base text-gray-500 leading-relaxed flex-1">
                          {module.description}
                        </p>
                      )}
                      <div className="mt-3 xl:mt-5 pt-2 xl:pt-3 invisible group-hover:visible transition-all flex-shrink-0">
                        <span className="text-red-600 text-xs md:text-sm xl:text-base font-medium flex items-center justify-center gap-1">
                          Acceder <span className="material-icons text-base md:text-lg">arrow_forward</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Señalador bookmark estilo scoring */}
                {module.badge && (
                  <div className="absolute right-2 flex items-start gap-2 z-10" style={{ top: '2px' }}>
                    <div className="relative group/rechazado">
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 invisible group-hover/rechazado:opacity-100 group-hover/rechazado:visible transition-all duration-300 whitespace-nowrap pointer-events-none">
                        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-semibold rounded-lg px-3 py-2 shadow-xl">
                          <span className="flex items-center gap-1.5">
                            <span className="material-icons text-sm">cancel</span>
                            Rechazados: {module.badge.rechazados}
                          </span>
                        </div>
                      </div>
                      <div className="cursor-help transition-all duration-300 ease-in-out hover:-translate-y-1">
                        <div className="bg-gradient-to-b from-red-600 to-red-700 shadow-lg flex items-center justify-center"
                             style={{
                               width: '38px',
                               height: '55px',
                               clipPath: 'polygon(0 0, 100% 0, 100% 85%, 50% 100%, 0 85%)',
                               borderTopLeftRadius: '4px',
                               borderTopRightRadius: '4px',
                               paddingTop: '12px'
                             }}>
                          <span className="text-base font-bold text-white leading-none">
                            {module.badge.rechazados}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default CesionesDashboard;
